import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Check, ChevronRight, FileText, GitBranch, Save,
} from 'lucide-react';

import api from '@/lib/api';
import { instructionFieldNames, resolvePickedPipeline } from '@/lib/processOrder';
import ProcessOrderPicker from '@/components/phone/ProcessOrderPicker';
import { FieldRenderer, fieldName, fieldType } from '@/components/phone/FieldControls';
import {
  ScreenLoader, Spinner, TabRow, TopBar,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * Filling in a job card, on a phone.
 *
 * This screen was the desktop form dropped into the phone column: an 8rem label
 * column beside 24px-tall inputs at 12px type, two to a row, with the pipeline
 * in a rail that had nowhere to go. It also read `sections` and `fields` off a
 * response that returns `field_sections` and `job_card`, so it rendered an empty
 * form under a blank title — nothing to fill in and no way to tell why.
 *
 * So: the label sits above its field, every input is a 48px tap target, and the
 * sections are cards. The pipeline is a strip of chips at the top that re-reads
 * as fields change — the one thing worth seeing while editing, because a field
 * that routes the job silently rewrites the list of processes it will run.
 *
 * A job card is two decisions, so the screen is two tabs. Processes is what it
 * runs through, picked and ordered by hand; Details is what it is made of. They
 * were one scroll, which put the pipeline — the decision a supervisor opens this
 * to change — below a form of up to forty fields.
 *
 * Picking processes is why this saves through `/job-cards/{id}/fill` rather than
 * the specs-only `/mobile/job-cards/{id}`: the numbering IS the pipeline, and
 * only fill carries it.
 *
 * Mirrors `mobile-frontend/app/job-card/edit/[id].tsx`, which has the fields but
 * not the picker — the phone build routes off the specifications alone.
 */

const PREVIEW_DEBOUNCE_MS = 300;

export default function SupervisorJobCardEditPage() {
  const { id: jobCardId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState(null);
  const [specs, setSpecs] = useState({});
  const [pipeline, setPipeline] = useState([]);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [processOrder, setProcessOrder] = useState({});
  const [rules, setRules] = useState(null);
  const [tab, setTab] = useState('processes');

  const fetchEditData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/mobile/job-cards/${jobCardId}/edit`);
      const data = res.data;
      setEditData(data);

      // The card's saved specs, topped up with any current_value the field
      // config carries that the card has never had written to it.
      const values = { ...(data.job_card?.specifications || {}) };
      Object.values(data.field_sections || {}).forEach((fields) => {
        (fields || []).forEach((field) => {
          const name = fieldName(field);
          if (field.current_value !== undefined && field.current_value !== null
              && values[name] === undefined) {
            values[name] = field.current_value;
          }
        });
      });
      setSpecs(values);
      setProcessOrder(data.job_card?.process_order || {});
      setPipeline(data.pipeline_preview || data.job_card?.production_stages || []);

      // The processes this product type can run. Without them there is nothing
      // to pick from, and `rules` stays null so the save leaves whatever
      // numbering the card already had alone rather than clearing it.
      const code = data.job_card?.product_type_code;
      if (code) {
        try {
          const config = await api.get(`/product-configs/${code}`);
          setRules(config.data?.stage_generation_rules || []);
        } catch {
          setRules(null);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load job card data');
    }
    setLoading(false);
  }, [jobCardId]);

  useEffect(() => { fetchEditData(); }, [fetchEditData]);

  const productTypeCode = editData?.job_card?.product_type_code;

  const hasPicks = Object.keys(processOrder).length > 0;

  /**
   * What the job will run through.
   *
   * A numbered list IS the pipeline, and it resolves here with no round trip —
   * the same resolution the server makes from the same numbers, so moving a row
   * redraws the strip under your thumb. Only a card with nothing numbered falls
   * back to the rules, and those have to be asked for because they read the
   * specifications.
   */
  const pickedPipeline = useMemo(
    () => (hasPicks ? resolvePickedPipeline(processOrder, rules || []) : []),
    [hasPicks, processOrder, rules],
  );

  useEffect(() => {
    if (hasPicks || !productTypeCode || Object.keys(specs).length === 0) return undefined;
    const timer = setTimeout(async () => {
      setPipelineLoading(true);
      try {
        const res = await api.post(`/job-cards/pipeline-preview/${productTypeCode}`, {
          field_values: specs,
        });
        setPipeline(res.data.stages || []);
      } catch {
        // the committed pipeline is the honest fallback when the preview refuses
      }
      setPipelineLoading(false);
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [specs, productTypeCode, hasPicks]);

  const setField = (name, value) => setSpecs((prev) => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { order_type: productTypeCode, specifications: specs };
      // Only send a numbering that was picked against a list actually loaded.
      // Sending {} because the rules never arrived — or because this type has
      // none — would clear a pipeline the supervisor was never shown.
      if (Array.isArray(rules) && rules.length > 0) body.process_order = processOrder;
      const res = await api.put(`/job-cards/${jobCardId}/fill`, body);
      toast.success(res.data?.production_started
        ? 'Saved — the item is on the floor'
        : 'Job card updated');
      navigate(`/supervisor/job-card/${jobCardId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  // Fields the process list owns. They are the pipeline decision written as a
  // spec, so showing them in the form as well asks the same question twice and
  // lets the two answers disagree — the desktop picker drops them for the same
  // reason.
  const ownedByProcesses = useMemo(() => instructionFieldNames(rules || []), [rules]);

  const sections = useMemo(() => {
    const grouped = editData?.field_sections || {};
    return Object.entries(grouped)
      .map(([name, fields]) => ({
        name,
        fields: (fields || []).filter((f) => !ownedByProcesses.has(fieldName(f))),
      }))
      .filter((section) => section.fields.length > 0);
  }, [editData, ownedByProcesses]);

  const fieldCount = sections.reduce((n, s) => n + s.fields.length, 0);
  const filled = sections.reduce(
    (n, s) => n + s.fields.filter((f) => {
      const v = specs[fieldName(f)];
      return v !== undefined && v !== null && String(v).trim() !== '';
    }).length,
    0,
  );

  if (loading) return <ScreenLoader />;

  if (!editData) {
    return (
      <div className="flex flex-1 flex-col">
        <TopBar title="Edit Job Card" onBack={() => navigate(-1)} />
        <div className="flex flex-1 flex-col items-center justify-center py-24">
          <FileText className="h-16 w-16" style={{ color: Colors.text.tertiary }} strokeWidth={1.2} />
          <p className="mt-4 text-[15px] font-medium" style={{ color: Colors.text.secondary }}>
            Job card not found
          </p>
        </div>
      </div>
    );
  }

  const jobCard = editData.job_card || {};
  // A card whose product type carries no field config still has whatever specs
  // were written to it, and those must stay editable rather than vanish.
  const looseSpecs = sections.length === 0 ? Object.keys(specs) : [];
  // a numbered list wins; nothing numbered leaves the rules' answer standing
  const shownPipeline = hasPicks ? pickedPipeline : pipeline;
  const canPickProcesses = Array.isArray(rules) && rules.length > 0;
  // with nothing to pick from there is no second tab, so the form is the screen
  const showDetails = !canPickProcesses || tab === 'details';

  const saveButton = (
    <button
      type="button"
      data-testid="save-job-card"
      onClick={handleSave}
      disabled={saving}
      className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
      style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
    >
      {saving
        ? <Spinner size="sm" className="!border-white !border-b-transparent" />
        : <><Check className="h-4 w-4" /> Save</>}
    </button>
  );

  return (
    <div className="flex flex-1 flex-col">
      <TopBar
        eyebrow="EDIT JOB CARD"
        title={jobCard.order_id || 'Job Card'}
        subtitle={jobCard.item_name || jobCard.product_type_name}
        onBack={() => navigate(-1)}
        actions={saveButton}
      />

      <div className="flex-1 px-4 pb-10">
        {/* What it will run through, as it is being decided */}
        {shownPipeline.length > 0 && (
          <section
            className="mt-3 rounded-[14px] border p-4"
            style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
          >
            <div className="flex items-center gap-2">
              <GitBranch className="h-4 w-4" style={{ color: Colors.accent.DEFAULT }} />
              <h2 className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>
                Pipeline preview
              </h2>
              {pipelineLoading && <Spinner size="sm" />}
            </div>
            <div
              data-testid="pipeline-preview"
              className="-mx-4 mt-2 flex items-center gap-1 overflow-x-auto px-4 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {shownPipeline.map((stage, idx) => (
                <React.Fragment key={stage.stage_name || idx}>
                  <span
                    className="shrink-0 rounded-md border px-2 py-1 text-[11px] font-medium"
                    style={{
                      backgroundColor: stage.is_completed ? Colors.semantic.positiveWash
                        : stage.is_active ? Colors.accent.wash : Colors.background.secondary,
                      borderColor: stage.is_completed ? '#D2E2D8'
                        : stage.is_active ? '#CFDEE8' : Colors.border.default,
                      color: stage.is_completed ? Colors.semantic.positive
                        : stage.is_active ? Colors.accent.DEFAULT : Colors.text.secondary,
                    }}
                  >
                    {stage.stage_name || stage.name || String(stage)}
                  </span>
                  {idx < shownPipeline.length - 1 && (
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" style={{ color: Colors.text.tertiary }} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="mt-2 text-[10px]" style={{ color: Colors.text.tertiary }}>
              {shownPipeline.length} stage{shownPipeline.length === 1 ? '' : 's'}
              {hasPicks ? ' · picked by hand' : ' · routed off the specifications'} · updates on save
            </p>
          </section>
        )}

        {/* Two decisions, two tabs — the pipeline used to sit under a form of
            up to forty fields, which is the wrong way round for the thing a
            supervisor opens this screen to change. */}
        {canPickProcesses && (
          <div className="mt-3">
            <TabRow
              tabs={[
                { key: 'processes', label: 'Processes', testID: 'edit-tab-processes' },
                { key: 'details', label: 'Details', testID: 'edit-tab-details' },
              ]}
              active={tab}
              onSelect={setTab}
            />
          </div>
        )}

        {canPickProcesses && tab === 'processes' && (
          <ProcessOrderPicker
            rules={rules}
            processOrder={processOrder}
            onChange={setProcessOrder}
            specs={specs}
            onSpecChange={setField}
          />
        )}

        {showDetails && sections.map((section) => (
          <section
            key={section.name}
            data-testid={`section-${section.name}`}
            className="mt-3 rounded-[14px] border p-4"
            style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
          >
            <h2 className="mb-3 text-[15px] font-bold" style={{ color: Colors.text.primary }}>
              {section.name}
            </h2>
            <div className="grid grid-cols-2 gap-x-3 gap-y-4">
              {section.fields.map((field) => {
                const name = fieldName(field);
                const type = fieldType(field);
                // a long value needs the width; a paper size does not
                const wide = ['textarea', 'multiline'].includes(type);
                return (
                  <div key={name} className={wide ? 'col-span-2' : ''}>
                    <div className="mb-1.5 flex items-center gap-1">
                      <span
                        className="min-w-0 flex-1 truncate text-[11px] font-medium capitalize"
                        style={{ color: Colors.text.secondary }}
                      >
                        {(field.label || name || '').replace(/_/g, ' ')}
                      </span>
                      {field.required && (
                        <span className="text-[11px]" style={{ color: Colors.status.error }}>*</span>
                      )}
                      {field.is_auto_filled && (
                        <span
                          className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-medium"
                          style={{ backgroundColor: Colors.accent.wash, color: Colors.accent.DEFAULT }}
                        >
                          Auto
                        </span>
                      )}
                    </div>
                    <FieldRenderer
                      field={field}
                      value={specs[name]}
                      onChange={(v) => setField(name, v)}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {showDetails && looseSpecs.length > 0 && (
          <section
            className="mt-3 rounded-[14px] border p-4"
            style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
          >
            <h2 className="mb-3 text-[15px] font-bold" style={{ color: Colors.text.primary }}>
              Specifications
            </h2>
            <div className="grid grid-cols-2 gap-x-3 gap-y-4">
              {looseSpecs.map((key) => (
                <div key={key}>
                  <p className="mb-1.5 truncate text-[11px] font-medium capitalize" style={{ color: Colors.text.secondary }}>
                    {key.replace(/_/g, ' ')}
                  </p>
                  <FieldRenderer
                    field={{ name: key, label: key }}
                    value={specs[key]}
                    onChange={(v) => setField(key, v)}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {showDetails && sections.length === 0 && looseSpecs.length === 0 && (
          <p className="py-16 text-center text-[13px]" style={{ color: Colors.text.tertiary }}>
            This product type has no job card fields configured.
          </p>
        )}

        {/* the same button as the bar, at the end of a long form — a supervisor
            who has scrolled to the bottom should not scroll back to save */}
        {(sections.length > 0 || looseSpecs.length > 0 || canPickProcesses) && (
          <button
            type="button"
            data-testid="save-job-card-bottom"
            onClick={handleSave}
            disabled={saving}
            className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-[10px] text-[15px] font-bold disabled:opacity-60"
            style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
          >
            {saving
              ? <Spinner size="sm" className="!border-white !border-b-transparent" />
              : <><Save className="h-4 w-4" /> Save changes</>}
          </button>
        )}

        {showDetails && fieldCount > 0 && (
          <p className="mt-3 text-center text-[11px]" style={{ color: Colors.text.tertiary }}>
            {filled} of {fieldCount} fields filled
          </p>
        )}
      </div>
    </div>
  );
}
